import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { TargetAssignmentsKpi } from "@/actions/ssr/target/getTargetAssignmentsKpiSSR";
import type { TargetAssignmentsResponse } from "@/actions/ssr/target/getTargetAssignmentsSSR";
import type {
  PortalTargetDetailApiShape,
  PortalTargetEntriesResponse,
} from "@/components/target/portal/portalTargetTypes";

interface PortalTargetState {
  kpi: TargetAssignmentsKpi;
  list: TargetAssignmentsResponse;
  detail: PortalTargetDetailApiShape | null;
  entries: PortalTargetEntriesResponse | null;
  listClientFetched: boolean;
  kpiClientFetched: boolean;
  entriesClientFetched: boolean;
}

const initialState: PortalTargetState = {
  kpi: {
    total_assigned: 0,
    total_assigned_trend_pct: 0,
    active_targets: 0,
    active_near_completion: 0,
    completion_rate_pct: 0,
    completion_rate_trend_pct: 0,
    pending_reviews: 0,
    total_completed: 0,
  },
  list: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: 1,
    results: [],
    sales_target_count: 0,
    visit_count: 0,
    general_count: 0,
    collection_count: 0,
  },
  detail: null,
  entries: null,
  listClientFetched: false,
  kpiClientFetched: false,
  entriesClientFetched: false,
};

const portalTargetSlice = createSlice({
  name: "portalTarget",
  initialState,
  reducers: {
    setPortalTargetKpi(state, action: PayloadAction<TargetAssignmentsKpi>) {
      state.kpiClientFetched = true;
      state.kpi = action.payload;
    },
    setPortalTargetKpiFromSSR(
      state,
      action: PayloadAction<TargetAssignmentsKpi>,
    ) {
      if (state.kpiClientFetched) return;
      state.kpi = action.payload;
    },
    setPortalTargetList(
      state,
      action: PayloadAction<TargetAssignmentsResponse>,
    ) {
      state.listClientFetched = true;
      state.list = action.payload;
    },
    setPortalTargetListFromSSR(
      state,
      action: PayloadAction<TargetAssignmentsResponse>,
    ) {
      if (state.listClientFetched) return;
      state.list = action.payload;
    },
    setPortalTargetDetail(
      state,
      action: PayloadAction<PortalTargetDetailApiShape | null>,
    ) {
      state.detail = action.payload;
    },
    setPortalTargetEntries(
      state,
      action: PayloadAction<PortalTargetEntriesResponse | null>,
    ) {
      state.entriesClientFetched = true;
      state.entries = action.payload;
    },
    setPortalTargetEntriesFromSSR(
      state,
      action: PayloadAction<PortalTargetEntriesResponse | null>,
    ) {
      if (state.entriesClientFetched) return;
      state.entries = action.payload;
    },
    patchPortalTargetListRow(
      state,
      action: PayloadAction<{ id: string; status: string; progress: number }>,
    ) {
      const row = state.list.results.find((r) => r.id === action.payload.id);
      if (row) {
        row.status = action.payload.status as typeof row.status;
        row.progress = action.payload.progress;
      }
    },
  },
});

export const {
  setPortalTargetKpi,
  setPortalTargetKpiFromSSR,
  setPortalTargetList,
  setPortalTargetListFromSSR,
  setPortalTargetDetail,
  setPortalTargetEntries,
  setPortalTargetEntriesFromSSR,
  patchPortalTargetListRow,
} = portalTargetSlice.actions;
export default portalTargetSlice.reducer;
